
import { Field, Form, Formik, useFormikContext } from "formik";
import React, { useCallback, useEffect, useRef, useState, Fragment } from "react";
import AddButton from "../buttons/button";
import moment from "moment";
import { debounce } from "lodash";
import errorToast from "../helper-components/error-toast";
import warningToast from "../helper-components/warning-toast";
import { useDispatch } from "react-redux";
import { purchaseTaxCalAction,purchaseItemQtyConversionAction } from "../../redux/actions/purchase-action";
import {
  purchaseBatchPreTax,
  purchaseBatchWithTax,
  purchaseDocumentTotals,
  purchaseRowWithBatches,
} from "../../utils/purchase-batch-calculations";
import {
  binLocationListAction,
  searchBinLocationAction,
} from "../../redux/actions/general-action";
import * as Yup from "yup";
import { Dialog, Transition } from "@headlessui/react";
import CloseIcon from "../../assets/icons/close-icon";
import InputField from "../forms/input-field";
import SelectField from "../forms/select-field";
import { ChevronDownIcon, PlusIcon, SquarePlus, Package,SquarePen,X } from "lucide-react";
import ToggleSwitch from "../forms/toggle-switch";
import {
  decimalNumberFn,
  minDate,
  positiveIntegerFn,
} from "../helper-components/helper-function";
import LoadingOverlay from "../helper-components/overlay-loading";
// Repairs a batch whose `to_bin_location` holds the bin id instead of its
// location code, so the value that gets submitted matches the name shown in
// the To Bin dropdown rather than re-saving the id.
const BinLabelSync = ({ binNameById }) => {
  const { values, setFieldValue } = useFormikContext();
  const binId = values?.to_bin_id;
  const resolved = binId ? binNameById?.[binId] : undefined;

  useEffect(() => {
    if (!resolved) return;
    if (values?.to_bin_location === resolved) return;
    if (values?.to_bin_location && values?.to_bin_location !== binId) return;
    setFieldValue("to_bin_location", resolved, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binId, resolved, values?.to_bin_location]);

  return null;
};

const PtrAutoCalc = () => {
  const { values, setFieldValue } = useFormikContext();

  const mrp = Number(values.item_batch_unit_price) || 0;
  const ptr_entered = Number(values.item_batch_ptr_amount) || 0;
  const tax_pct = Number(values.item_batch_tax_percentage) || 0;
  const edit_ptr = values.gst_inc
    ? ptr_entered
    : ptr_entered * (1 + tax_pct / 100); // PTR
  const pr = Number(values.item_batch_purhcase_rate_before_tax) || 0; // PR excl GST
   useEffect(() => {
    
    if (!mrp && !edit_ptr && !pr) return;

    const id = setTimeout(() => {
      const retailer_margin = mrp > 0 ? ((mrp - edit_ptr) / mrp) * 100 : 0;
      const margin = edit_ptr > 0 ? ((edit_ptr - pr) / edit_ptr) * 100 : 0;
      const markup = pr > 0 ? ((edit_ptr - pr) / pr) * 100 : 0;
      setFieldValue("retailer_margin", Number(retailer_margin.toFixed(2)));
      setFieldValue("margin", Number(margin.toFixed(2)));
      setFieldValue("markup", Number(markup.toFixed(2)));
    }, 100);

  return () => clearTimeout(id);
}, [mrp, edit_ptr, pr, setFieldValue]);
return null};
const PtsAutoCalc = () => {
  const { values, setFieldValue } = useFormikContext();

  const mrp = Number(values.item_batch_unit_price) || 0;
  // PTS used for margin maths. When pts_gst_inc is true the entered PTS already
  // includes GST, so it's used as-is. When false the entered PTS excludes GST,
  // so the tax is added virtually (not shown in the UI) before calculating.
  const pts_entered = Number(values.item_batch_pts) || 0;
  const tax_pct = Number(values.item_batch_tax_percentage) || 0;
  const edit_pts = values.pts_gst_inc
    ? pts_entered
    : pts_entered * (1 + tax_pct / 100); // PTS
  const pr = Number(values.item_batch_purhcase_rate_before_tax) || 0; // PR excl GST
  useEffect(() => {

    if (!mrp && !edit_pts && !pr) return;

    const id = setTimeout(() => {
      const stockist_margin = mrp > 0 ? ((mrp - edit_pts) / mrp) * 100 : 0;
      const pts_margin = edit_pts > 0 ? ((edit_pts - pr) / edit_pts) * 100 : 0;
      const pts_markup = pr > 0 ? ((edit_pts - pr) / pr) * 100 : 0;
      setFieldValue("stockist_margin", Number(stockist_margin.toFixed(2)));
      setFieldValue("pts_margin", Number(pts_margin.toFixed(2)));
      setFieldValue("pts_markup", Number(pts_markup.toFixed(2)));
    }, 100);

  return () => clearTimeout(id);
}, [mrp, edit_pts, pr, setFieldValue]);
return null};
const PurchaseBatchesPanel = ({
  setOpen,
  itemId,
  binLocationList = [],
  disabled = false,
  itemValues = {},
  open,
  prefix = "gort",
  searchRef,
  // Batch number from a scanned barcode — opens a new batch with it filled in
  prefillBatchNumber = "",
}) => {
  const batchRef = useRef();
  const [loading, setLoading] = useState(false);
  const [openBatchIndex, setOpenBatchIndex] = useState(null);
  const [clickedPTR, setClickedPTR] = useState(false);
  const [clickedPTS, setClickedPTS] = useState(false);
  const [purchaseBatchConfig,setpurchaseBatchConfig]=useState([]);
  const defaultSelectedItem = itemValues?.data?.find((item) => itemId === item?.item_id);
  useEffect(() => {
      const data = localStorage.getItem("ModPermission");
      if (data) {
        const parsed = JSON.parse(data);
        const billingData = parsed.find((obj) => obj?.purchase_batch_input_panel);
        if (billingData?.purchase_batch_input_panel) {
          setpurchaseBatchConfig(billingData.purchase_batch_input_panel);
        }
      }
    }, [loading]);
  // Resolve base_quantity from uom_dropdown first (most reliable source),
  // then fall back to the item-level base_quantity field.
  const resolvedBaseQty =
    (
      defaultSelectedItem?.uom_dropdown?.find(
        (uom) => uom?.item_uom_id === defaultSelectedItem?.item_uom_id
      ) ||
      defaultSelectedItem?.uom_dropdown?.find(
        (uom) => uom?.label === defaultSelectedItem?.item_uom
      )
    )?.base_quantity ||
    defaultSelectedItem?.base_quantity ||
    1;

  // The scanned batch number only fills the first new batch of this opening
  const [pendingBatchNumber, setPendingBatchNumber] = useState("");
  const [newBatch, setNewBatch] = useState(
    defaultSelectedItem?.item_batches?.length > 0 ? false : true
  );
  // Runs on open, and again when a scan points the open panel at another item
  // or batch. A scanned batch the item already has is shown expanded with its
  // saved values; any other scanned batch starts a new batch with it filled in.
  useEffect(() => {
    if (open) {
      const savedBatchIndex = prefillBatchNumber
        ? (defaultSelectedItem?.item_batches ?? []).findIndex(
            (batch) => batch?.item_batch_number === prefillBatchNumber
          )
        : -1;
      if (savedBatchIndex >= 0) {
        setPendingBatchNumber("");
        setNewBatch(false);
        setOpenBatchIndex(savedBatchIndex);
        return;
      }
      setPendingBatchNumber(prefillBatchNumber || "");
      setNewBatch(
        prefillBatchNumber || !(defaultSelectedItem?.item_batches?.length > 0)
          ? true
          : false
      );
      setOpenBatchIndex(null);
      setTimeout(() => batchRef.current?.focus(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId, prefillBatchNumber]);

  const { setFieldValue } = useFormikContext();
  const batches = defaultSelectedItem?.item_batches;

  const dispatch = useDispatch();

  // A batch can reach us with the bin's id sitting in `to_bin_location` (saved
  // documents have been written that way), which renders as a raw uuid in the
  // To Bin dropdown. Keep an id -> location-code map for the warehouse so the
  // name can always be recovered from `to_bin_id`.
  const [binNameById, setBinNameById] = useState({});
  useEffect(() => {
    if (!open) return;
    dispatch(
      binLocationListAction(
        (res) =>
          setBinNameById(
            (res?.data ?? []).reduce((acc, bin) => {
              if (bin?.bin_no_id) acc[bin.bin_no_id] = bin?.bin_location_no;
              return acc;
            }, {})
          ),
        () => setBinNameById({})
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Bin name to show for a batch — resolved from the id, never the raw id. */
  const resolveBinLabel = (binId, storedLabel) => {
    if (binId && binNameById[binId]) return binNameById[binId];
    if (!storedLabel || storedLabel === binId) return "";
    return storedLabel;
  };

  const fetchBinOptions = (inputValue, callback) => {
    if (!inputValue?.length) {
      callback([]);
      return;
    }
    dispatch(
      searchBinLocationAction(
        { field: "bin_location_no", value: inputValue },
        (data) =>
          callback(
            (data?.data ?? []).map((bin) => ({
              label: bin?.bin_location_no,
              value: bin?.bin_no_id,
              actualLabel: bin?.bin_location_no,
            }))
          ),
        () => callback([])
      )
    );
  };
  const loadBinOptions = useCallback(
    debounce((inputValue, callback) => fetchBinOptions(inputValue, callback), 500),
    []
  );

  const defaultSelectedItemIndex = itemValues?.data?.findIndex(
    (item) => itemId === item?.item_id
  );
  const uom__id = itemValues?.data[defaultSelectedItemIndex]?.item_uom_id;
  const validationSchema = Yup.object().shape({
    item_batch_number: Yup.string()
      .transform((v) => (typeof v === "string" ? v.trim() : v))
      .required("Batch number is required"),
    item_sellable_quantity: Yup.number()
      .typeError("Must be a number")
      .required("Purchase quantity is required")
      .moreThan(0, "Purchase quantity must be greater than 0"),
    item_batch_purchase_rate: Yup.number()
      .typeError("Must be a number")
      .required("Purchase rate is required")
      .moreThan(0, "Purchase rate must be greater than 0"),
    item_batch_unit_price: Yup.number()
      .typeError("Must be a number")
      .required("MRP is required")
      .moreThan(0, "MRP must be greater than 0")
      .test(
        "MRP-greater-than-final-purchase-rate",
        "MRP must be greater than the final purchase rate",
        function (value) {
          const { item_batch_purchase_rate } = this?.parent;
          // if purchase rate empty, keep Yup required errors responsible
          if (!value || !item_batch_purchase_rate) return true;
          return Number(value) > Number(item_batch_purchase_rate);
        }
      ),
    item_batch_free_quantity: Yup.number()
      .transform((value) =>
        typeof value === "string" ? parseFloat(value, 10) : value
      )
      .typeError("Must be a number"),
    item_exp_date: Yup.date().required("Expiration date is required"),
  });
  const today = new Date();
  const sixMonthsLater = new Date(today.setMonth(today.getMonth() + 6));

  const initialValues =
    {
      item_id: defaultSelectedItem?.item_id,
      item_batch_number: pendingBatchNumber || "",
      item_sellable_quantity: "",
      item_non_sellable_quantity: "",
      item_batch_free_quantity: "",
      item_batch_total_effective_qauntity: 0,
      item_batch_purchase_rate: "",
      item_batch_total_base_purchase_rate: 0,
      item_batch_for_qauntity: "",
      item_batch_for_free_qauntity: "",
      item_batch_scheme_discount_percentage: "",
      item_batch_scheme_discount_amount: 0,
      item_batch_scheme_purchase_rate: 0,
      item_batch_discount_percentage: "",
      item_batch_gen_discount_amount: 0,
      item_batch_discounted_purchase_rate: "",
      item_batch_discount_amount: 0,
      item_batch_tax_percentage: "",
      item_batch_tax_amount: 0,
      item_batch_final_purchase_rate: 0,
      item_batch_unit_price: "",
      item_batch_total_purchase_rate: 0,
      item_batch_margin_percentage: 0,
      item_exp_date: moment(sixMonthsLater).format("YYYY-MM-DD"),
      item_mfg_date: "2023/12/02",
      from_bin_location: "",
      to_bin_location: itemValues?.default_dropping_bin?.bin_location_no,
      to_bin_id: itemValues?.default_dropping_bin?.bin_no_id,
      from_bin_id: "",
      item_batch_purhcase_rate_before_tax: 0,
      gst_type:itemValues?.is_inter_state===true?"Inter":"Intra",
      gst_inc: true,
      pts_gst_inc: true,
    } ?? {};

  const handleQuantityChange = async (
    qty,
    pr = 0,
    dis = 0,
    item_batch_for_qauntity = 0,
    item_batch_for_free_qauntity = 0,
    item_batch_free_quantity = 0,
    setValues,
    values,
    schemePer = 0,
    item_batch_unit_price,
    gst_type,
    extraFields = {}
  ) => {
    let updatedData = { ...values };
    const inputs = {
      qty,
      pr,
      dis,
      forQty: item_batch_for_qauntity,
      forFreeQty: item_batch_for_free_qauntity,
      freeQty: item_batch_free_quantity,
      schemePer,
      unitPrice: item_batch_unit_price,
    };
    const preTax = purchaseBatchPreTax(inputs);

    setLoading(true);

    dispatch(
      purchaseTaxCalAction(
        {
          item_id: defaultSelectedItem?.item_id,
          purchase_rate: preTax.taxablePurchaseRate,
          is_inter_state: itemValues?.is_inter_state === true,
        },
        (data) => {
          setLoading(false);

          const updatedDetails = purchaseBatchWithTax(
            updatedData,
            inputs,
            preTax,
            data.data,
            extraFields
          );

          setValues(updatedDetails);
        },
        () => {
          setLoading(false);
        }
      )
    );
  };

  // ✅ toast always based on FRESH errors (from validateForm)
  const handleShowErrorToast = (errors) => {
    const e = errors || {};
    if (e.item_batch_number) return errorToast(e.item_batch_number);
    if (e.item_exp_date) return errorToast(e.item_exp_date);
    if (e.item_sellable_quantity) return errorToast(e.item_sellable_quantity);
    if (e.item_batch_purchase_rate) return errorToast(e.item_batch_purchase_rate);
    if (e.item_batch_unit_price) return errorToast(e.item_batch_unit_price);
    if (e.item_batch_free_quantity) return errorToast(e.item_batch_free_quantity);
  };

  const handleDeleteBatch = (batchIndex) => {
    const updatedBatches = [...(batches || [])];
    updatedBatches.splice(batchIndex, 1);

    const sumQuantity = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_sellable_quantity) || 0),
      0
    );
    const totalDiscount = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_discount_amount) || 0),
      0
    );
    const totalTax = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_tax_amount) || 0),
      0
    );
    const totalSGST = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_sgst_amount) || 0),
      0
    );
    const totalCGST = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_cgst_amount) || 0),
      0
    );
    const totalIGST = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_igst_amount) || 0),
      0
    );
    const totalAmt = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_total_purchase_rate) || 0),
      0
    );
    const totalFreeQty = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_free_quantity) || 0),
      0
    );

    const updatedDataRow = {
      ...defaultSelectedItem,
      item_batches: updatedBatches,
      quantity: sumQuantity,
      item_free_quantity: +totalFreeQty,
      gort_total_discount: +totalDiscount,
      item_discount_amount: +totalDiscount,
      item_tax_amount: +totalTax?.toFixed(2),
      item_gst: updatedBatches?.[0]?.item_total_tax_percentage || 0,
      item_sgst: totalSGST,
      item_cgst: totalCGST,
      item_igst: totalIGST,
      item_pack_size: resolvedBaseQty * sumQuantity,
      item_total_amount: totalAmt.toFixed(2),
    };

    setFieldValue(`data.${defaultSelectedItemIndex}`, updatedDataRow);

    const updatedData = [...(itemValues?.data || [])];
    updatedData[defaultSelectedItemIndex] = updatedDataRow;

    const raw_total_amount = updatedData.reduce(
      (sum, item) => sum + (parseFloat(item.item_total_amount) || 0),
      0
    );
    const total_tax = updatedData.reduce(
      (sum, item) => sum + (parseFloat(item.item_tax_amount) || 0),
      0
    );
    const total_discount = updatedData.reduce(
      (sum, item) => sum + (parseFloat(item.item_discount_amount) || 0),
      0
    );

    const sub_total = (raw_total_amount - total_tax + total_discount).toFixed(2);
    const total_amount = Math.round(raw_total_amount).toFixed(2);
    const round_off_value = (Math.round(raw_total_amount) - raw_total_amount).toFixed(2);

    setFieldValue(`${prefix}_sub_total`, sub_total);
    setFieldValue(`${prefix}_total_gst`, total_tax);
    setFieldValue(`${prefix}_total_discount`, total_discount);
    setFieldValue(`${prefix}_total_amount`, total_amount);
    setFieldValue(`${prefix}_round_off_value`, round_off_value);
    setFieldValue(`${prefix}_tcs_or_tds_amount`, 0);
    setFieldValue(`${prefix}_tcs_or_tds_percentage_id`, "");
    setFieldValue(`${prefix}_tcs_or_tds_percentage`, 0);
    setFieldValue(`${prefix}_tds_tcs_type`, "");

    setNewBatch(false);
    setOpenBatchIndex(null);
  };

  const innerOnSubmit = (values) => {
    const item_batches = [...(batches || [])];

    if (
      typeof openBatchIndex === "number" &&
      openBatchIndex >= 0 &&
      openBatchIndex < item_batches.length
    ) {
      item_batches[openBatchIndex] = values;
    } else {
      item_batches.push(values);
    }

    const updatedDataRow = purchaseRowWithBatches(
      defaultSelectedItem,
      item_batches,
      resolvedBaseQty
    );

    setFieldValue(`data.${defaultSelectedItemIndex}`, updatedDataRow);

    const updatedData = [...(itemValues?.data || [])];
    updatedData[defaultSelectedItemIndex] = updatedDataRow;

    Object.entries(purchaseDocumentTotals(updatedData, prefix)).forEach(
      ([field, value]) => setFieldValue(field, value)
    );

    setPendingBatchNumber("");
    setNewBatch(false);
    setOpenBatchIndex(null);
  };

  const defaultInputClass =
    "min-h-[42px] rounded-[8px] px-2 bg-[var(--vert-input-bg)] text-[var(--vert-input-color)] text-[length:var(--vert-input-font-size)] font-[var(--vert-input-font-weight)] rounded input-field-no-spin-buttons placeholder:text-[var(--vert-input-place-color)] placeholder:text-[length:var(--vert-input-place-font-size)] placeholder:font-[var(--vert-input-place-font-weight)] focus:outline-none focus:border-[var(--vert-input-focus)] focus:text-[var(--vert-input-color)] focus:bg-[var(--vert-input-bg)]";
  const labelClass =
    "whitespace-nowrap flex text-[var(--vert-input-label-color)] text-[length:var(--vert-input-label-font-size)] font-[var(--vert-input-label-font-weight)]";
  const errorBorder = "border border-[var(--vert-input-error-border)]";
  const inputBorder = "border border-[var(--vert-input-border)]";

  // ---- Customization config (from ./dummy) ----------------------------------
  // Single source of truth: a flat, ordered array. Order, visibility (enabled),
  // width (full) and label/placeholder text all come from purchaseBatchConfig.
  // Functionality is untouched.
  const configFields = Array.isArray(purchaseBatchConfig)
    ? purchaseBatchConfig
    : [];
  const fieldCfgMap = configFields.reduce((acc, f) => {
    if (f?.value) acc[f.value] = f;
    // Include nested fields (e.g. scheme's Sch Qty / Sch Free Qty) so their
    // per-field flags (decimal, etc.) are resolvable too.
    (f?.childrenHeading || []).forEach((c) => {
      if (c?.value) acc[c.value] = c;
    });
    return acc;
  }, {});
  // Footer totals config (single enabled flag, row labels in childrenHeading).
  const summaryCfg =
    configFields.find((c) => c?.value === "summary") || {
      enabled: false,
      childrenHeading: [],
    };
  // Width class for a field (full -> w-100%, otherwise w-50%).
  const fieldWidth = (name) =>
    fieldCfgMap[name]?.full ? "w-[100%]" : "w-[50%]";
  const fieldText = (name, fallback) => fieldCfgMap[name]?.label ?? fallback;
  // A field accepts decimals only when its own DECIMAL toggle is on in the
  // customization. Nothing else grants them: "item_sellable_quantity" and
  // "item_batch_free_quantity" used to be force-allowed here so their sum could
  // be validated as a whole number, which meant "." stayed typable even with
  // DECIMAL switched off for those fields.
  const allowsDecimal = (name) => fieldCfgMap[name]?.decimal === true;
  const qtyType = (name) => (allowsDecimal(name) ? "float" : "number");
  const qtyKeyGuard = (name) =>
    allowsDecimal(name) ? decimalNumberFn : positiveIntegerFn;
  // Pack Qty + Free Pack Qty must add up to a whole number.
  const isPackSumWhole = (a, b) =>
    Number.isInteger((Number(a) || 0) + (Number(b) || 0));
  const fracComplement = (v) => {
    const n = Number(v) || 0;
    return +(Math.ceil(n) - n).toFixed(2);
  };
  // A decimal Purchase Qty (10.5) can only be saved with a Free Qty that
  // makes the total whole, so Free Qty becomes mandatory - shown as an orange
  // warning, not a red error. Names the smallest Free Qty that fixes it.
  const isDecimalQty = (v) => !Number.isInteger(Number(v) || 0);
  const packSumWarning = (qty, free) => {
    const need = fracComplement((Number(qty) || 0) + (Number(free) || 0));
    return `Purchase Qty is ${+qty || 0} - add ${need} more Free Qty (or adjust the qty) so Purchase Qty + Free Qty is a whole number.`;
  };
  const isFieldEnabled = (name) => {
    if (configFields.length === 0) return true;
    const cfg = fieldCfgMap[name];
    return !!cfg && cfg.enabled !== false;
  };
  // ---------------------------------------------------------------------------

  const toggleBatch = (index) => {
    setOpenBatchIndex((prev) => (prev === index ? null : index));
  };

  const expiryRef = useRef();
  const binRef = useRef();
  const freeQtyRef = useRef();
  const mrpRef = useRef();
  const ptrRef = useRef();
  const ptsRef = useRef();
  const basePrRef = useRef();
  const schemeQtyRef = useRef();
  const schemeFreeQtyRef = useRef();
  const schemePerRef = useRef();
  const DisPerRef = useRef();

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          setOpen(false);
        }}
      >
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl">
                <Formik
                  enableReinitialize={true}
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={innerOnSubmit}
                >
                  {({
                    values,
                    handleSubmit,
                    errors,
                    setFieldValue: setFV,
                    setValues,
                    validateForm,
                    setTouched,
                  }) => {
                    // Renders a single batch field by its config value. All
                    // calculation / focus / keyboard behaviour is preserved
                    // verbatim; only label, placeholder and width come from
                    // purchaseBatchConfig.
                    const renderBatchField = (cfg) => {
                      const value = cfg?.value;
                      const text = fieldText(value, cfg?.label);
                      const width = fieldWidth(value);
                      switch (value) {
                        case "item_batch_number":
                          return (
                            <InputField
                              key={value}
                              maxLength={20}
                              autoFocus={true}
                              inputRef={batchRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              isRequired={true}
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              type="text"
                              name="item_batch_number"
                              placeholder={text}
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              inputMode="text"
                              pattern="[A-Za-z0-9]*"
                              onChange={(e) => {
                                const raw = e.target.value ?? "";
                                const clean = raw.replace(/[^A-Za-z0-9]/g, "");
                                if (clean !== raw && raw !== "") {
                                  errorToast("Only alphanumeric characters are allowed");
                                }
                                setFV("item_batch_number", clean);
                              }}
                              onPaste={(e) => {
                                const pasted = (e.clipboardData.getData("text") || "").replace(
                                  /[^A-Za-z0-9]/g,
                                  ""
                                );
                                e.preventDefault();
                                setFV("item_batch_number", pasted);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  expiryRef.current?.focus();
                                }
                              }}
                            />
                          );
                        case "item_exp_date":
                          return (
                            <InputField
                              key={value}
                              inputRef={expiryRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type="date"
                              isRequired={true}
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_exp_date"
                              placeholder={text}
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              min={minDate}
                              onKeyDown={(e) => {
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  binRef.current?.focus();
                                }
                              }}
                            />
                          );
                        case "to_bin_id":
                          return (
                            <SelectField
                              key={value}
                              ref={binRef}
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              isDisabled={disabled}
                              placeholder={text}
                              loadOptions={loadBinOptions}
                              defaultOptions={false}
                              label={text}
                              id={`to_bin_id`}
                              name={`to_bin_id`}
                              isSearchable={true}
                              isRequired={false}
                              defaultValue={{
                                label: resolveBinLabel(
                                  values?.to_bin_id,
                                  values?.to_bin_location
                                ),
                                value: values?.to_bin_id ?? "",
                              }}
                              handleSelected={(selectedoption) => {
                                setFV(`to_bin_location`, selectedoption?.actualLabel);
                                setFV(`to_bin_id`, selectedoption?.value);
                              }}
                              errMsgContainerClass="hidden"
                            />
                          );
                        // case "item_batch_pack_quantity":
                        //   return (
                        //     <InputField
                        //       key={value}
                        //       defaultInputClass={defaultInputClass}
                        //       labelClass={labelClass}
                        //       errorBorder={errorBorder}
                        //       inputBorder={inputBorder}
                        //       type={qtyType(value)}
                        //       isRequired={true}
                        //       containerClass={width}
                        //       parentClass="flex flex-col gap-2"
                        //       isDisabled={false}
                        //       name="item_batch_pack_quantity"
                        //       placeholder={text}
                        //       label={text}
                        //       labelWidth="6.5rem"
                        //       errMsgContainerClass="hidden"
                        //       onBlur={(e) => {
                        //         const value = +(e.currentTarget.value || 0);
                        //         // Route the fractional part of the purchase qty to
                        //         // the Free Qty section, then convert BOTH sections so
                        //         // each populates its pack/loose fields.
                        //         const frac = +(value - Math.floor(value)).toFixed(2);
                        //         if (
                        //           frac > 0 &&
                        //           isFieldEnabled("item_batch_pack_free_quantity") &&
                        //           allowsDecimal("item_batch_free_quantity")
                        //         ) {
                        //           setFV("item_batch_free_quantity", frac);
                                  
                        //         }
                                
                        //       }}
                        //       onKeyDown={(e) => {
                        //         qtyKeyGuard(value)(e);
                        //         if (e.key === "Tab" || e.key === "Enter") {
                        //           e.preventDefault();
                        //           // Fire the conversion (onBlur) on Enter even when
                        //           // the next field isn't rendered (ref would be null).
                        //           e.currentTarget.blur();
                        //           freeQtyRef.current?.focus();
                        //         }
                        //       }}
                        //     />
                        //   );
                        case "item_sellable_quantity":
                          return (
                            <InputField
                              key={value}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type={qtyType(value)}
                              isRequired={false}
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_sellable_quantity"
                              placeholder={text}
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              onBlur={(e) => {
                                const value = +(e.currentTarget.value || 0);
                                // Pack Qty + Free Pack Qty must stay a whole number.
                                // Don't overwrite Free Qty here — just warn so the user
                                // can correct it (the submit guard blocks too).
                                if (
                                  !isPackSumWhole(value, values?.item_batch_free_quantity)
                                ) {
                                  warningToast(
                                    packSumWarning(
                                      value,
                                      values?.item_batch_free_quantity
                                    )
                                  );
                                }
                                // Changing the qty after the Base PR is in must
                                // recalculate the totals too - before, they only
                                // updated when the Base PR field was blurred.
                                if (values?.item_batch_purchase_rate !== "" && values?.item_batch_purchase_rate != null) {
                                  handleQuantityChange(
                                    value,
                                    parseFloat(values?.item_batch_purchase_rate || 0),
                                    parseFloat(values?.item_batch_discount_percentage || 0) / 100,
                                    parseInt(values?.item_batch_for_qauntity || 0),
                                    parseInt(values?.item_batch_for_free_qauntity || 0),
                                    parseFloat(values?.item_batch_free_quantity || 0),
                                    setValues,
                                    values,
                                    parseFloat(values?.item_batch_scheme_discount_percentage || 0),
                                    parseFloat(values?.item_batch_unit_price || 0),
                                    values.gst_type
                                  );
                                }
                              }}
                              onKeyDown={(e) => {
                                qtyKeyGuard(value)(e);
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  // Fire the conversion (onBlur) on Enter even when
                                  // the next field isn't rendered (ref would be null).
                                  e.currentTarget.blur();
                                  (freeQtyRef.current || mrpRef.current)?.focus();
                                }
                              }}
                            />
                          );
                        // case "item_batch_loose_quantity":
                        //   return (
                        //     <InputField
                        //       key={value}
                        //       defaultInputClass={defaultInputClass}
                        //       labelClass={labelClass}
                        //       errorBorder={errorBorder}
                        //       inputBorder={inputBorder}
                        //       type={qtyType(value)}
                        //       isRequired={false}
                        //       containerClass={width}
                        //       parentClass="flex flex-col gap-2"
                        //       isDisabled={false}
                        //       name="item_batch_loose_quantity"
                        //       placeholder={text}
                        //       label={text}
                        //       labelWidth="6.5rem"
                        //       errMsgContainerClass="hidden"
                        //       handleChange={(e) => {}}
                        //       onKeyDown={(e) => {
                        //         qtyKeyGuard(value)(e);
                        //         if (e.key === "Tab" || e.key === "Enter") {
                        //           e.preventDefault();
                        //           // Fire the conversion (onBlur) on Enter even when
                        //           // the next field isn't rendered (ref would be null).
                        //           e.currentTarget.blur();
                        //           mrpRef.current?.focus();
                        //         }
                        //       }}
                        //       onBlur={(e) => {
                        //         const value = +(e.currentTarget.value || 0);
                                
                        //         mrpRef.current?.focus();
                        //       }}
                        //     />
                        //   );
                        // case "item_batch_pack_free_quantity":
                        //   return (
                        //     <InputField
                        //       key={value}
                        //       inputRef={freeQtyRef}
                        //       defaultInputClass={defaultInputClass}
                        //       labelClass={labelClass}
                        //       errorBorder={errorBorder}
                        //       inputBorder={inputBorder}
                        //       type={qtyType(value)}
                        //       isRequired={false}
                        //       containerClass={width}
                        //       parentClass="flex flex-col gap-2"
                        //       isDisabled={false}
                        //       name="item_batch_pack_free_quantity"
                        //       placeholder={text}
                        //       label={text}
                        //       labelWidth="6.5rem"
                        //       errMsgContainerClass="hidden"
                        //       handleChange={(e) => {}}
                        //       onKeyDown={(e) => {
                        //         qtyKeyGuard(value)(e);
                        //         if (e.key === "Tab" || e.key === "Enter") {
                        //           e.preventDefault();
                        //           // Fire the conversion (onBlur) on Enter even when
                        //           // the next field isn't rendered (ref would be null).
                        //           e.currentTarget.blur();
                        //           mrpRef.current?.focus();
                        //         }
                        //       }}
                        //       onBlur={(e) => {
                        //         const value = +(e.currentTarget.value || 0);
                                
                        //         mrpRef.current?.focus();
                        //       }}
                        //     />
                        //   );
                        case "item_batch_free_quantity":
                          return (
                            <InputField
                              key={value}
                              inputRef={freeQtyRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              // Orange while a decimal Purchase Qty still
                              // needs Free Qty to make the total whole.
                              inputBorder={
                                !isPackSumWhole(
                                  values?.item_sellable_quantity,
                                  values?.item_batch_free_quantity
                                )
                                  ? "border border-[var(--dashboard-warning)]"
                                  : inputBorder
                              }
                              // Decimal Purchase Qty -> Free Qty is mandatory
                              // and must accept decimals (0.5), even when its
                              // own DECIMAL toggle is off.
                              type={
                                isDecimalQty(values?.item_sellable_quantity)
                                  ? "float"
                                  : qtyType(value)
                              }
                              isRequired={isDecimalQty(
                                values?.item_sellable_quantity
                              )}
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_batch_free_quantity"
                              placeholder={
                                isDecimalQty(values?.item_sellable_quantity)
                                  ? `Required - e.g. ${fracComplement(
                                      values?.item_sellable_quantity
                                    )}`
                                  : text
                              }
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              onBlur={(e) => {
                                const value = +(e.currentTarget.value || 0);
                                if (
                                  !isPackSumWhole(values?.item_sellable_quantity, value)
                                ) {
                                  warningToast(
                                    packSumWarning(
                                      values?.item_sellable_quantity,
                                      value
                                    )
                                  );
                                }
                                // Free Qty changes the effective qty (and so the
                                // final PR / margin) - recalculate like the qty.
                                if (values?.item_batch_purchase_rate !== "" && values?.item_batch_purchase_rate != null) {
                                  handleQuantityChange(
                                    +values?.item_sellable_quantity || 0,
                                    parseFloat(values?.item_batch_purchase_rate || 0),
                                    parseFloat(values?.item_batch_discount_percentage || 0) / 100,
                                    parseInt(values?.item_batch_for_qauntity || 0),
                                    parseInt(values?.item_batch_for_free_qauntity || 0),
                                    value,
                                    setValues,
                                    values,
                                    parseFloat(values?.item_batch_scheme_discount_percentage || 0),
                                    parseFloat(values?.item_batch_unit_price || 0),
                                    values.gst_type
                                  );
                                }
                              }}
                              onKeyDown={(e) => {
                                (isDecimalQty(values?.item_sellable_quantity)
                                  ? decimalNumberFn
                                  : qtyKeyGuard(value))(e);
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  // Blur first so onBlur recalculates, then move
                                  // on to MRP (focusing this same field kept the
                                  // cursor stuck here and never blurred).
                                  e.currentTarget.blur();
                                  mrpRef.current?.focus();
                                }
                              }}
                            />
                          );
                        // case "item_batch_free_loose_quantity":
                        //   return (
                        //     <InputField
                        //       key={value}
                        //       defaultInputClass={defaultInputClass}
                        //       labelClass={labelClass}
                        //       errorBorder={errorBorder}
                        //       inputBorder={inputBorder}
                        //       type={qtyType(value)}
                        //       isRequired={false}
                        //       containerClass={width}
                        //       parentClass="flex flex-col gap-2"
                        //       isDisabled={false}
                        //       name="item_batch_free_loose_quantity"
                        //       placeholder={text}
                        //       label={text}
                        //       labelWidth="6.5rem"
                        //       errMsgContainerClass="hidden"
                        //       handleChange={(e) => {}}
                        //       onKeyDown={(e) => {
                        //         qtyKeyGuard(value)(e);
                        //         if (e.key === "Tab" || e.key === "Enter") {
                        //           e.preventDefault();
                        //           const looseQty = +(e.currentTarget.value || 0);
                                  
                        //           mrpRef.current?.focus();
                        //         }
                        //       }}
                        //       onBlur={(e) => {
                        //         const value = +(e.currentTarget.value || 0);
                                
                        //         mrpRef.current?.focus();
                        //       }}
                        //     />
                        //   );
                        case "item_batch_unit_price":
                          return (
                            <InputField
                              key={value}
                              inputRef={mrpRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type="float"
                              isRequired={true}
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_batch_unit_price"
                              placeholder={text}
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              onKeyDown={(e) => {
                                decimalNumberFn(e);
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  basePrRef.current?.focus();
                                }
                              }}
                            />
                          );
                        case "item_batch_purchase_rate":
                          return (
                            <InputField
                              key={value}
                              inputRef={basePrRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type="number"
                              isRequired={true}
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_batch_purchase_rate"
                              placeholder={text}
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              onKeyDown={(e) => {
                                decimalNumberFn(e);
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  schemeQtyRef.current?.focus();
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.currentTarget.value || 0;
                                handleQuantityChange(
                                  +values?.item_sellable_quantity || 0,
                                  value !== "" ? parseFloat(value) : value,
                                  parseFloat(values?.item_batch_discount_percentage || 0) / 100,
                                  parseInt(values?.item_batch_for_qauntity || 0),
                                  parseInt(values?.item_batch_for_free_qauntity || 0),
                                  parseFloat(values?.item_batch_free_quantity || 0),
                                  setValues,
                                  values,
                                  parseFloat(values?.item_batch_scheme_discount_percentage || 0),
                                  parseFloat(values?.item_batch_unit_price || 0),
                                  values.gst_type
                                );
                              }}
                            />
                          );
                        case "item_batch_ptr_amount":
                          return (
                            <InputField
                              key={value}
                              inputRef={ptrRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type="number"
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_batch_ptr_amount"
                              placeholder={text}
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              rightIcon={
                                <SquarePen
                                  onClick={() => {
                                    setClickedPTR((prev) => !prev);
                                    setClickedPTS(false);
                                  }}
                                  size={18}
                                  className="cursor-pointer"
                                />
                              }
                              handleChange={(e) => {}}
                              onKeyDown={(e) => {
                                decimalNumberFn(e);
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  const value = e.currentTarget.value || 0;
                                  ptsRef.current?.focus();
                                }
                              }}
                            />
                          );
                        case "item_batch_pts":
                          return (
                            <InputField
                              key={value}
                              inputRef={ptsRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type="number"
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_batch_pts"
                              placeholder={text}
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              rightIcon={
                                <SquarePen
                                  onClick={() => {
                                    setClickedPTS((prev) => !prev);
                                    setClickedPTR(false);
                                  }}
                                  size={18}
                                  className="cursor-pointer"
                                />
                              }
                              handleChange={(e) => {}}
                              onKeyDown={(e) => {
                                decimalNumberFn(e);
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  schemeQtyRef.current?.focus();
                                }
                              }}
                            />
                          );
                        case "item_batch_discount_percentage":
                          return (
                            <InputField
                              key={value}
                              maxValue={100}
                              inputRef={DisPerRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type="number"
                              isRequired={false}
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_batch_discount_percentage"
                              placeholder={text}
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              onKeyDown={(e) => {
                                decimalNumberFn(e);
                              }}
                              onBlur={(e) => {
                                const value = e.currentTarget.value || 0;
                                handleQuantityChange(
                                  +values?.item_sellable_quantity || 0,
                                  parseFloat(values?.item_batch_purchase_rate || 0),
                                  parseFloat(value) / 100,
                                  parseInt(values?.item_batch_for_qauntity || 0),
                                  parseInt(values?.item_batch_for_free_qauntity || 0),
                                  parseFloat(values?.item_batch_free_quantity || 0),
                                  setValues,
                                  values,
                                  parseFloat(values?.item_batch_scheme_discount_percentage || 0),
                                  parseFloat(values?.item_batch_unit_price || 0),
                                  values.gst_type
                                );
                              }}
                            />
                          );
                        case "item_batch_tax_percentage":
                          return (
                            <InputField
                              key={value}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type="number"
                              isRequired={false}
                              containerClass={width}
                              parentClass="flex flex-col gap-2"
                              isDisabled={true}
                              name="item_batch_tax_percentage"
                              placeholder={text}
                              label={text}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              onKeyDown={decimalNumberFn}
                            />
                          );
                        default:
                          return null;
                      }
                    };

                    // Scheme row: Sch Qty + Sch Free Qty (left half) and Scheme %
                    // (right half). Inner labels come from the scheme config.
                    const renderSchemeRow = (cfg) => {
                      const schemeText = (v, fb) =>
                        (cfg?.childrenHeading || []).find((f) => f?.value === v)?.label ?? fb;
                      return (
                        <div key="scheme-row" className="flex gap-5 ">
                          <div className="flex gap-3 w-[50%] items-center">
                            <InputField
                              inputRef={schemeQtyRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type="number"
                              isRequired={false}
                              containerClass="w-[50%]"
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_batch_for_qauntity"
                              placeholder={schemeText("item_batch_for_qauntity", "Sch Qty")}
                              label={schemeText("item_batch_for_qauntity", "Sch Qty")}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              onKeyDown={(e) => {
                                positiveIntegerFn(e);
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  schemeFreeQtyRef.current?.focus();
                                }
                              }}
                              onBlur={(e) => {
                                const value = +(e.currentTarget.value || 0);
                                handleQuantityChange(
                                  +values?.item_sellable_quantity || 0,
                                  parseFloat(values?.item_batch_purchase_rate || 0),
                                  parseFloat(values?.item_batch_discount_percentage || 0) / 100,
                                  parseInt(value),
                                  parseInt(values?.item_batch_for_free_qauntity || 0),
                                  parseFloat(values?.item_batch_free_quantity || 0),
                                  setValues,
                                  values,
                                  0,
                                  parseFloat(values?.item_batch_unit_price || 0),
                                  values.gst_type
                                );
                              }}
                            />
                            <div className="mt-5">
                              <SquarePlus color="#6B7280" />
                            </div>
                            <InputField
                              inputRef={schemeFreeQtyRef}
                              defaultInputClass={defaultInputClass}
                              labelClass={labelClass}
                              errorBorder={errorBorder}
                              inputBorder={inputBorder}
                              type="number"
                              isRequired={false}
                              containerClass="w-[50%]"
                              parentClass="flex flex-col gap-2"
                              isDisabled={false}
                              name="item_batch_for_free_qauntity"
                              placeholder={schemeText("item_batch_for_free_qauntity", "Sch Free Qty")}
                              label={schemeText("item_batch_for_free_qauntity", "Sch Free Qty")}
                              labelWidth="6.5rem"
                              errMsgContainerClass="hidden"
                              onKeyDown={(e) => {
                                positiveIntegerFn(e);
                                if (e.key === "Tab" || e.key === "Enter") {
                                  e.preventDefault();
                                  schemePerRef.current?.focus();
                                }
                              }}
                              onBlur={(e) => {
                                const value = +(e.currentTarget.value || 0);
                                handleQuantityChange(
                                  +values?.item_sellable_quantity || 0,
                                  parseFloat(values?.item_batch_purchase_rate || 0),
                                  parseFloat(values?.item_batch_discount_percentage || 0) / 100,
                                  parseInt(values?.item_batch_for_qauntity || 0),
                                  parseInt(value),
                                  parseFloat(values?.item_batch_free_quantity || 0),
                                  setValues,
                                  values,
                                  0,
                                  parseFloat(values?.item_batch_unit_price || 0),
                                  values.gst_type
                                );
                              }}
                            />
                          </div>

                          <InputField
                            inputRef={schemePerRef}
                            defaultInputClass={defaultInputClass}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            isRequired={false}
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={false}
                            name="item_batch_scheme_discount_percentage"
                            placeholder={schemeText("item_batch_scheme_discount_percentage", "Scheme %")}
                            label={schemeText("item_batch_scheme_discount_percentage", "Scheme %")}
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            onKeyDown={(e) => {
                              decimalNumberFn(e);
                              if (e.key === "Tab" || e.key === "Enter") {
                                e.preventDefault();
                                DisPerRef.current?.focus();
                              }
                            }}
                            onBlur={(e) => {
                              const value = +(e.currentTarget.value || 0);
                              handleQuantityChange(
                                +values?.item_sellable_quantity || 0,
                                parseFloat(values?.item_batch_purchase_rate || 0),
                                parseFloat(values?.item_batch_discount_percentage || 0) / 100,
                                0,
                                0,
                                parseFloat(values?.item_batch_free_quantity || 0),
                                setValues,
                                values,
                                parseFloat(value),
                                parseFloat(values?.item_batch_unit_price || 0),
                                values.gst_type
                              );
                            }}
                          />
                        </div>
                      );
                    };

                    // Walk the config in order: half-width fields pair two-per-row,
                    // full-width fields stand alone, the scheme row renders in place,
                    // and the PTR/PTS editor panels follow the PTR/PTS row.
                    const renderBatchBody = () => {
                      const out = [];
                      let pending = [];
                      const flushPending = () => {
                        for (let i = 0; i < pending.length; i += 2) {
                          const pair = pending.slice(i, i + 2);
                          out.push(
                            <div key={`prow-${out.length}`} className="flex gap-5">
                              {pair}
                            </div>
                          );
                        }
                        pending = [];
                      };

                      configFields.forEach((cfg) => {
                        // Summary renders in its own block below the form body.
                        if (cfg?.value === "summary") return;
                        if (cfg?.value === "scheme") {
                          flushPending();
                          if (cfg?.enabled !== false) out.push(renderSchemeRow(cfg));
                          return;
                        }
                        if (!isFieldEnabled(cfg?.value)) return;
                        const node = renderBatchField(cfg);
                        if (!node) return;
                        if (cfg?.full) {
                          flushPending();
                          out.push(node);
                        } else {
                          pending.push(node);
                        }
                        if (cfg?.value === "item_batch_pts") {
                          flushPending();
                          if (clickedPTR) out.push(renderPtrPanel());
                          if (clickedPTS) out.push(renderPtsPanel());
                        }
                      });
                      flushPending();
                      return out;
                    };

                    // PTR editor panel (opened from the PTR pencil icon) — unchanged.
                    const renderPtrPanel = () => (
                      <div key="ptr-panel" className="border border-[var(--button-border-color)] rounded-md p-3 py-4">
                        <div className="flex justify-between mb-4">
                          <div className="font-bold text-sml">
                            Price-To-Retailer (PTR)
                          </div>
                          <div>
                            <X
                              onClick={() => {
                                setClickedPTR((prev) => !prev);
                              }}
                              className="cursor-pointer"
                              size={14}
                            />
                          </div>
                        </div>

                        <div className="flex gap-5 mb-4">
                          <InputField
                            defaultInputClass={defaultInputClass}
                            isReadOnly={true}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={true}
                            isRequired={true}
                            name="item_batch_unit_price"
                            placeholder="MRP"
                            label="MRP (INCL GST)"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              e.preventDefault();
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />

                          <InputField
                            inputRef={mrpRef}
                            defaultInputClass={defaultInputClass}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            isRequired={true}
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={true}
                            isReadOnly={true}
                            name="item_batch_tax_percentage"
                            placeholder="GST %"
                            label="GST %"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />
                        </div>

                        <div className="flex gap-5 mb-4">
                          <InputField
                            defaultInputClass={defaultInputClass}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={false}
                            isRequired={true}
                            name="retailer_margin"
                            placeholder="Margin %"
                            label="Retailers Margin% WRT MRP"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />

                          <div>
                            <div className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] mb-4">
                              GST
                            </div>
                            <div className="flex">
                              <ToggleSwitch
                                checked={values.gst_inc}
                                onClick={() => {
                                  setFV("gst_inc", !values.gst_inc);
                                }}
                                primaryText=""
                                secondaryText="Is PTR inclusive of GST"
                                secondaryTextClass="text-[var(--toggle-switch-text-fg)] text-[length:var(--toggle-switch-text-fs)] font-[var(--toggle-switch-text-fw)] text-sm"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-5 mb-4">
                          <InputField
                            defaultInputClass={defaultInputClass}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={false}
                            isRequired={true}
                            name="item_batch_ptr_amount"
                            placeholder="PTR"
                            label="Price-To-Retailers"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />

                          <InputField
                            defaultInputClass={defaultInputClass}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            isRequired={true}
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={false}
                            name="margin"
                            placeholder="Margin %"
                            label="Your Margin % WRT PTR"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />
                        </div>

                        <InputField
                          defaultInputClass={defaultInputClass}
                          labelClass={labelClass}
                          errorBorder={errorBorder}
                          inputBorder={inputBorder}
                          type="number"
                          isRequired={true}
                          containerClass="w-[100%]"
                          parentClass="flex flex-col gap-2"
                          isDisabled={false}
                          name="markup"
                          placeholder="Markup %"
                          label="Your Markup % WRT Purchase Rate"
                          labelWidth="6.5rem"
                          errMsgContainerClass="hidden"
                          handleChange={(e, field) => {
                            setFV(field.name, e.target.value);
                          }}
                          onKeyDown={decimalNumberFn}
                        />

                        <div className="mb-4" />

                        <InputField
                          inputRef={mrpRef}
                          defaultInputClass={defaultInputClass}
                          labelClass={labelClass}
                          errorBorder={errorBorder}
                          inputBorder={inputBorder}
                          type="number"
                          isReadOnly={true}
                          isDisabled={true}
                          isRequired={true}
                          containerClass="w-[100%]"
                          parentClass="flex flex-col gap-2"
                          name="item_batch_purhcase_rate_before_tax"
                          placeholder="Purchase Rate"
                          label="Purchase Rate (EXCL GST)"
                          labelWidth="6.5rem"
                          errMsgContainerClass="hidden"
                          handleChange={(e, field) => {
                            setFV(field.name, e.target.value);
                          }}
                          onKeyDown={(e) => {
                            decimalNumberFn(e);
                            if (e.key === "Tab" || e.key === "Enter") {
                              e.preventDefault();
                              schemeQtyRef.current?.focus();
                            }
                          }}
                        />

                        <div className="flex mt-4 justify-end">
                          <button className="bg-primary text-white p-3 px-4 border border-primary rounded-md">
                            Save
                          </button>
                        </div>
                      </div>
                    );

                    // PTS editor panel (opened from the PTS pencil icon) —
                    // mirrors the PTR panel but drives PTS-specific fields.
                    const renderPtsPanel = () => (
                      <div key="pts-panel" className="border border-[var(--button-border-color)] rounded-md p-3 py-4">
                        <div className="flex justify-between mb-4">
                          <div className="font-bold text-sml">
                            Price-To-Stockist (PTS)
                          </div>
                          <div>
                            <X
                              onClick={() => {
                                setClickedPTS((prev) => !prev);
                              }}
                              className="cursor-pointer"
                              size={14}
                            />
                          </div>
                        </div>

                        <div className="flex gap-5 mb-4">
                          <InputField
                            defaultInputClass={defaultInputClass}
                            isReadOnly={true}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={true}
                            isRequired={true}
                            name="item_batch_unit_price"
                            placeholder="MRP"
                            label="MRP (INCL GST)"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              e.preventDefault();
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />

                          <InputField
                            defaultInputClass={defaultInputClass}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            isRequired={true}
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={true}
                            isReadOnly={true}
                            name="item_batch_tax_percentage"
                            placeholder="GST %"
                            label="GST %"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />
                        </div>

                        <div className="flex gap-5 mb-4">
                          <InputField
                            defaultInputClass={defaultInputClass}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={false}
                            isRequired={true}
                            name="stockist_margin"
                            placeholder="Margin %"
                            label="Stockist Margin% WRT MRP"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />

                          <div>
                            <div className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] mb-4">
                              GST
                            </div>
                            <div className="flex">
                              <ToggleSwitch
                                checked={values.pts_gst_inc}
                                onClick={() => {
                                  setFV("pts_gst_inc", !values.pts_gst_inc);
                                }}
                                primaryText=""
                                secondaryText="Is PTS inclusive of GST"
                                secondaryTextClass="text-[var(--toggle-switch-text-fg)] text-[length:var(--toggle-switch-text-fs)] font-[var(--toggle-switch-text-fw)] text-sm"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-5 mb-4">
                          <InputField
                            defaultInputClass={defaultInputClass}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={false}
                            isRequired={true}
                            name="item_batch_pts"
                            placeholder="PTS"
                            label="Price-To-Stockist"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />

                          <InputField
                            defaultInputClass={defaultInputClass}
                            labelClass={labelClass}
                            errorBorder={errorBorder}
                            inputBorder={inputBorder}
                            type="number"
                            isRequired={true}
                            containerClass="w-[50%]"
                            parentClass="flex flex-col gap-2"
                            isDisabled={false}
                            name="pts_margin"
                            placeholder="Margin %"
                            label="Your Margin % WRT PTS"
                            labelWidth="6.5rem"
                            errMsgContainerClass="hidden"
                            handleChange={(e, field) => {
                              setFV(field.name, e.target.value);
                            }}
                            onKeyDown={decimalNumberFn}
                          />
                        </div>

                        <InputField
                          defaultInputClass={defaultInputClass}
                          labelClass={labelClass}
                          errorBorder={errorBorder}
                          inputBorder={inputBorder}
                          type="number"
                          isRequired={true}
                          containerClass="w-[100%]"
                          parentClass="flex flex-col gap-2"
                          isDisabled={false}
                          name="pts_markup"
                          placeholder="Markup %"
                          label="Your Markup % WRT Purchase Rate"
                          labelWidth="6.5rem"
                          errMsgContainerClass="hidden"
                          handleChange={(e, field) => {
                            setFV(field.name, e.target.value);
                          }}
                          onKeyDown={decimalNumberFn}
                        />

                        <div className="mb-4" />

                        <InputField
                          defaultInputClass={defaultInputClass}
                          labelClass={labelClass}
                          errorBorder={errorBorder}
                          inputBorder={inputBorder}
                          type="number"
                          isReadOnly={true}
                          isDisabled={true}
                          isRequired={true}
                          containerClass="w-[100%]"
                          parentClass="flex flex-col gap-2"
                          name="item_batch_purhcase_rate_before_tax"
                          placeholder="Purchase Rate"
                          label="Purchase Rate (EXCL GST)"
                          labelWidth="6.5rem"
                          errMsgContainerClass="hidden"
                          handleChange={(e, field) => {
                            setFV(field.name, e.target.value);
                          }}
                          onKeyDown={(e) => {
                            decimalNumberFn(e);
                            if (e.key === "Tab" || e.key === "Enter") {
                              e.preventDefault();
                              schemeQtyRef.current?.focus();
                            }
                          }}
                        />

                        <div className="flex mt-4 justify-end">
                          <button className="bg-primary text-white p-3 px-4 border border-primary rounded-md">
                            Save
                          </button>
                        </div>
                      </div>
                    );

                    return (
                      <Form className="h-full relative">
                        {/* Header */}
                        <div
                          className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b"
                          style={{ borderColor: "var(--slide-panel-border)" }}
                        >
                          <BinLabelSync binNameById={binNameById} />
                          <PtrAutoCalc />
                          <PtsAutoCalc />
                          <div
                            style={{
                              maxHeight: "32px",
                              color: "var(--slide-panel-h-fg)",
                              fontSize: "var(--slide-panel-h-fs)",
                              fontWeight: "var(--slide-panel-h-fw)",
                            }}
                            className="min-w-[75%]"
                          >
                            {newBatch && typeof openBatchIndex === "number"
                              ? "Update Batch"
                              : newBatch
                              ? "Add New Batch"
                              : ""}
                          </div>

                          <AddButton
                            onclkFn={() => setOpen(false)}
                            icon={
                              <CloseIcon
                                width="12px"
                                height="12px"
                                fill="var(--close-button-fg)"
                              />
                            }
                            text={
                              <span>
                                Close <span className="opacity-50">(X)</span>
                              </span>
                            }
                            className="flex h-[32px] items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                          />
                        </div>

                        {newBatch ? (
                          <div
                            style={{
                              height: `calc(100% - 9rem)`,
                              minHeight: `calc(100% - 9rem)`,
                            }}
                            className="px-4 pt-5 relative"
                          >
                            <div className="h-full w-full flex flex-col gap-2 overflow-y-auto justify-between">
                              <div className="flex flex-col gap-2">
                                {renderBatchBody()}
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs font-medium text-gray-600">
                                    GST - Inter & Intra
                                  </label>
                                  <div className="flex items-center gap-6 mt-1">
                                    <label className="flex items-center gap-2 cursor-not-allowed opacity-60">
                                      <Field
                                        type="radio"
                                        name="gst_type"
                                        value="Intra"
                                        className="hidden peer"
                                        disabled
                                        checked={(itemValues?.is_inter_state !== true)}
                                      />
                                      <span className="w-3 h-3 rounded-full border border-gray-400 peer-checked:border-primary peer-checked:bg-primary transition-colors"></span>
                                      <span className="text-sm text-gray-800">Intra</span>
                                    </label>

                                    <label className="flex items-center gap-2 cursor-not-allowed opacity-60">
                                      <Field
                                        type="radio"
                                        name="gst_type"
                                        value="Inter"
                                        className="hidden peer"
                                        disabled
                                        checked={(itemValues?.is_inter_state === true)}
                                      />
                                      <span className="w-3 h-3 rounded-full border border-gray-400 peer-checked:border-primary peer-checked:bg-primary transition-colors"></span>
                                      <span className="text-sm text-gray-800">Inter</span>
                                    </label>
                                  </div>
                                </div>
                                {itemValues?.is_inter_state === false && (
                                  <div className="flex gap-5">
                                    <InputField
                                      maxValue={100}
                                      defaultInputClass={defaultInputClass}
                                      labelClass={labelClass}
                                      errorBorder={errorBorder}
                                      inputBorder={inputBorder}
                                      type="number"
                                      isRequired={false}
                                      containerClass="w-[50%]"
                                      parentClass="flex flex-col gap-2"
                                      isDisabled={true}
                                      name="item_batch_cgst_percentage"
                                      placeholder="CGST %"
                                      label="CGST %"
                                      labelWidth="6.5rem"
                                      errMsgContainerClass="hidden"
                                    />
                                    <InputField
                                      maxValue={100}
                                      defaultInputClass={defaultInputClass}
                                      labelClass={labelClass}
                                      errorBorder={errorBorder}
                                      inputBorder={inputBorder}
                                      type="number"
                                      isRequired={false}
                                      containerClass="w-[50%]"
                                      parentClass="flex flex-col gap-2"
                                      isDisabled={true}
                                      name="item_batch_sgst_percentage"
                                      placeholder="SGST %"
                                      label="SGST %"
                                      labelWidth="6.5rem"
                                      errMsgContainerClass="hidden"
                                    />
                                  </div>
                                )}

                                {itemValues?.is_inter_state === true && (
                                  <div className="flex gap-5">
                                    <InputField
                                      maxValue={100}
                                      defaultInputClass={defaultInputClass}
                                      labelClass={labelClass}
                                      errorBorder={errorBorder}
                                      inputBorder={inputBorder}
                                      type="number"
                                      isRequired={false}
                                      containerClass="w-[50%]"
                                      parentClass="flex flex-col gap-2"
                                      isDisabled={true}
                                      name="item_batch_igst_percentage"
                                      placeholder="IGST %"
                                      label="IGST %"
                                      labelWidth="6.5rem"
                                      errMsgContainerClass="hidden"
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Summary */}
                              {summaryCfg?.enabled && (
                              <div className="border border-[var(--slide-panel-border)] h-fit p-3 flex flex-col border-radius">
                                {(summaryCfg?.childrenHeading || []).map((sum, index) => {
                                  const gstKeys = [
                                    "item_batch_sgst_amount",
                                    "item_batch_cgst_amount",
                                    "item_batch_igst_amount",
                                  ];
                                  const isGst = gstKeys.includes(sum?.value);
                                  const summaryValue = (() => {
                                    switch (sum?.value) {
                                      case "item_batch_total_base_purchase_rate":
                                        return (
                                          values?.item_batch_total_base_purchase_rate ||
                                          values?.item_batch_total_purchase_rate ||
                                          0
                                        );
                                      case "item_batch_discounted_purchase_rate":
                                        return (
                                          values?.item_batch_discounted_purchase_rate ||
                                          values?.item_batch_taxable_amount ||
                                          0
                                        );
                                      case "item_batch_sgst_amount":
                                        return itemValues?.is_inter_state === false
                                          ? values?.item_batch_sgst_amount || 0
                                          : 0;
                                      case "item_batch_cgst_amount":
                                        return itemValues?.is_inter_state === false
                                          ? values?.item_batch_cgst_amount || 0
                                          : 0;
                                      case "item_batch_igst_amount":
                                        return itemValues?.is_inter_state === true
                                          ? values?.item_batch_igst_amount || 0
                                          : 0;
                                      default:
                                        return values?.[sum?.value] || 0;
                                    }
                                  })();
                                  return (
                                  <div
                                    key={index}
                                    className={`flex justify-between ${
                                      index === 5 ? "" : "border-b border-b-[var(--slide-panel-border)]"
                                    } p-3`}
                                  >
                                    <span
                                      className={`text-[var(--payment-summary-label-fg)]
                                                  text-[length:var(--payment-summary-label-fs)]
                                                  font-[var(--payment-summary-label-fw)]
                                                  ${isGst ? "ml-[30px]" : ""}`}
                                    >
                                      {sum?.label}
                                      {isGst && (
                                        <sup className="text-[10px] text-[#374151] bg-[#e5e7eb] rounded-full px-[5px] py-[1px] ml-1 align-super">
                                          i
                                        </sup>
                                      )}
                                    </span>

                                    <span className="text-[var(--payment-summary-value-fg)] text-[length:var(--payment-summary-value-fs)] font-[var(--payment-summary-value-fw)]">
                                      ₹ {summaryValue}
                                    </span>
                                  </div>
                                  );
                                })}
                              </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          // ✅ Batches list view (kept as your original logic, minimal changes)
                          <div
                            style={{
                              height: `calc(100% - 61px)`,
                              minHeight: `calc(100% - 61px)`,
                            }}
                            className="px-4 pt-5 relative"
                          >
                            <div className="h-[2rem] mb-[1rem] flex justify-between items-center">
                              <span className="text-[var(--slide-panel-h-fg)] text-[length:var(--slide-panel-h-fs)] font-[var(--slide-panel-h-fw)]">
                                Batches For {defaultSelectedItem?.item_name}
                              </span>

                              {!disabled && (
                                <AddButton
                                  type="button"
                                  icon={<PlusIcon size={18} fill="var(--primary-button-fg-color)" />}
                                  className="border-radius hover:bg-[var(--primary-button-hover-color)] flex justify-center gap-[8px] h-[36px] px-[12px] items-center whitespace-nowrap transition duration-300 ease-in-out text-[length:--primary-button-font-size] font-[var(--primary-button-font-weight)] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)]"
                                  onclkFn={() => {
                                    setNewBatch(true);
                                    setOpenBatchIndex(null);

                                    const preservedBatches = values.item_batches;
                                    const { item_batches: _, ...restInitials } = initialValues;

                                    setValues({
                                      ...restInitials,
                                      item_batches: preservedBatches,
                                    });

                                    setTimeout(() => batchRef.current?.focus(), 0);
                                  }}
                                  text={"Add New Batch"}
                                />
                              )}
                            </div>

                            <div
                              className="overflow-y-auto"
                              style={{
                                height: `calc(100% - 8rem)`,
                                minHeight: `calc(100% - 8rem)`,
                              }}
                            >
                              {batches?.map((batch, batchIndex) => {
                                const isOpen = openBatchIndex === batchIndex;

                                return (
                                  <div
                                    key={batchIndex}
                                    className="mt-4 border border-[var(--popover-border-color)] rounded-[8px] bg-[var(--slide-panel-accordian-bg)] overflow-hidden"
                                  >
                                    <div
                                      className={`h-[44px] cursor-pointer flex justify-between px-3 ${
                                        isOpen ? "border-b border-b-[var(--popover-border-color)]" : ""
                                      }`}
                                      onClick={() => toggleBatch(batchIndex)}
                                    >
                                      <div className="flex h-full items-center gap-2">
                                        <Package size={"20px"} color="var(--slide-panel-label)" />
                                        <span className="text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                          {batch.item_batch_number}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                        MRP : ₹{batch.item_batch_unit_price}
                                        <ChevronDownIcon
                                          className={`transition-transform duration-200 ${
                                            isOpen ? "rotate-180" : ""
                                          }`}
                                        />
                                      </div>
                                    </div>

                                    {isOpen && (
                                      <div className="p-3 bg-[var(--slide-panel-accordian-open-bg)]">
                                        <div className="flex flex-wrap">
                                          {[
                                            {
                                              label: "To Bin",
                                              value:
                                                resolveBinLabel(
                                                  batch?.to_bin_id,
                                                  batch?.to_bin_location
                                                ) || "-",
                                            },
                                            { label: "Purchase Qty", value: batch?.item_sellable_quantity || 0 },
                                            { label: "Free Qty", value: batch?.item_batch_free_quantity || 0 },
                                            { label: "MRP (Per 1 Qty)", value: batch?.item_batch_unit_price || 0 },
                                            { label: "Base PR (Per 1 Qty)", value: batch?.item_batch_purchase_rate || 0 },
                                            { label: "Discount %", value: batch?.item_batch_discount_percentage || 0 },
                                            { label: "Tax %", value: batch?.item_batch_tax_percentage || 0 },
                                            { label: "GST Type", value: itemValues?.is_inter_state === true ? "Inter" : "Intra" },
                                            ...(itemValues?.is_inter_state !== true ? [
                                              { label: "CGST %", value: batch?.item_batch_cgst_percentage || 0 },
                                              { label: "SGST %", value: batch?.item_batch_sgst_percentage || 0 },
                                            ] : [
                                              { label: "IGST %", value: batch?.item_batch_igst_percentage || 0 },
                                            ]),
                                          ].map((bat, idx) => (
                                            <div key={idx} className="flex flex-col gap-1 w-[50%] mt-[1rem]">
                                              <div className="text-[var(--slide-panel-label)] text-[length:var(--slide-panel-label-fs)] font-[var(--slide-label-value-fw)]">
                                                {bat.label}
                                              </div>
                                              <div className="text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)] text-wrap">
                                                {bat.value}
                                              </div>
                                            </div>
                                          ))}
                                        </div>

                                        <div className="border border-[var(--slide-panel-border)] h-fit p-3 flex flex-col border-radius mt-[1rem]">
                                          {[
                                            {
                                              label: "Base PR (Per 1 Qty)",
                                              value: batch?.item_batch_final_purchase_rate || 0,
                                            },
                                            {
                                              label: "Total Base PR",
                                              value: batch?.item_batch_total_base_purchase_rate || 0,
                                            },
                                            {
                                              label: "Total Discount Amount",
                                              value: batch?.item_batch_discount_amount || 0,
                                            },
                                            {
                                              label: "Taxable Amount",
                                              value: batch?.item_batch_discounted_purchase_rate || 0,
                                            },
                                            {
                                              label: "Tax Amount",
                                              value: batch?.item_batch_tax_amount || 0,
                                            },
                                            {
                                              label: "SGST",
                                              value: itemValues?.is_inter_state === false? (batch?.item_batch_sgst_amount || 0):0,
                                              isGst: true,
                                            },
                                            {
                                              label: "CGST",
                                              value: itemValues?.is_inter_state === false? (batch?.item_batch_cgst_amount || 0):0,
                                              isGst: true,
                                            },
                                            {
                                              label: "IGST",
                                              value: itemValues?.is_inter_state === true? (batch?.item_batch_igst_amount || 0):0,
                                              isGst: true,
                                            },
                                            {
                                              label: "Payable or Total Amt",
                                              value: batch?.item_batch_total_purchase_rate || 0,
                                            },
                                          ].map((sum, index, arr) => (
                                            <div
                                              key={index}
                                              className={`flex justify-between p-2 ${
                                                index === arr.length - 1
                                                  ? "border-t border-t-[var(--slide-panel-border)]"
                                                  : ""
                                              }`}
                                            >
                                              <span
                                                className={`text-[var(--payment-summary-label-fg)] text-[length:var(--payment-summary-label-fs)] font-[var(--payment-summary-label-fw)] ${
                                                  sum?.isGst ? "ml-[30px]" : ""
                                                }`}
                                              >
                                                {sum.label}
                                                {sum?.isGst && (
                                                  <sup className="text-[10px] text-[#374151] bg-[#e5e7eb] rounded-full px-[5px] py-[1px] ml-1 align-super">
                                                    i
                                                  </sup>
                                                )}
                                              </span>
                                              <span className="text-[var(--payment-summary-value-fg)] text-[length:var(--payment-summary-value-fs)] font-[var(--payment-summary-value-fw)]">
                                                ₹ {sum.value}
                                              </span>
                                            </div>
                                          ))}
                                        </div>

                                        {!disabled && (
                                          <div className="flex justify-between mt-[1rem]">
                                            <AddButton
                                              onclkFn={() => handleDeleteBatch(batchIndex)}
                                              prefixIcon={false}
                                              text={"Delete"}
                                              className="border rounded-[6px] border-[var(--status-badge-red-border)] text-[length:--status-badge-special-fs] font-[var(--status-badge-fw)] text-[var(--status-badge-red-fg)] bg-[var(--status-badge-red-bg)] px-3 h-[36px]"
                                              type="button"
                                            />

                                            <AddButton
                                              onclkFn={() => {
                                                setValues({
                                                  ...batch,
                                                  gst_type: itemValues?.is_inter_state === true ? "Inter" : "Intra",
                                                  item_batch_purhcase_rate_before_tax:
                                                    parseFloat(batch?.item_batch_purhcase_rate_before_tax) ||
                                                    parseFloat(batch?.item_batch_purchase_rate) ||
                                                    0,
                                                });
                                                setNewBatch(true);
                                                setOpenBatchIndex(batchIndex);
                                                setTimeout(() => batchRef.current?.focus(), 0);
                                              }}
                                              prefixIcon={false}
                                              text={"Edit Batch"}
                                              className="border rounded-[6px] border-[var(--status-badge-blue-border)] text-[length:--status-badge-special-fs] font-[var(--status-badge-fw)] text-[var(--status-badge-blue-fg)] bg-[var(--status-badge-blue-bg)] px-3 h-[36px]"
                                              type="button"
                                            />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Footer button */}
                        <div className="h-[5rem] absolute bottom-0 bg-white flex justify-between items-center px-3 w-full">
                          <div />

                          <AddButton
                            onclkFn={async () => {
                              if (newBatch) {
                                // ✅ 1) validate NOW, get fresh errors
                                const freshErrors = await validateForm();

                                // ✅ 2) mark required fields touched (so red border matches toast)
                                setTouched(
                                  {
                                    item_batch_number: true,
                                    item_exp_date: true,
                                    item_sellable_quantity: true,
                                    item_batch_purchase_rate: true,
                                    item_batch_unit_price: true,
                                  },
                                  true
                                );

                                // ✅ 3) if errors -> toast & stop
                                if (Object.keys(freshErrors || {}).length > 0) {
                                  handleShowErrorToast(freshErrors);
                                  return;
                                }

                                // ✅ 3a) Sellable Qty + Free Qty must be a whole number
                                if (
                                  !isPackSumWhole(
                                    values?.item_sellable_quantity,
                                    values?.item_batch_free_quantity
                                  )
                                ) {
                                  warningToast(
                                    packSumWarning(
                                      values?.item_sellable_quantity,
                                      values?.item_batch_free_quantity
                                    )
                                  );
                                  freeQtyRef.current?.focus();
                                  return;
                                }

                                // ✅ 4) proceed only when valid
                                await handleQuantityChange(
                                  +values?.item_sellable_quantity || 0,
                                  parseFloat(values?.item_batch_purchase_rate || 0),
                                  parseFloat(values?.item_batch_discount_percentage || 0) / 100,
                                  parseInt(values?.item_batch_for_qauntity || 0),
                                  parseInt(values?.item_batch_for_free_qauntity || 0),
                                  parseFloat(values?.item_batch_free_quantity || 0),
                                  setValues,
                                  values,
                                  parseFloat(values?.item_batch_scheme_discount_percentage || 0),
                                  parseFloat(values?.item_batch_unit_price || 0),
                                  values.gst_type
                                );

                                handleSubmit();
                              } else {
                                setOpen(false);
                                if (searchRef?.current) {
                                  setTimeout(() => searchRef.current?.focus(), 0);
                                }
                              }
                            }}
                            prefixIcon={false}
                            text={
                              newBatch && typeof openBatchIndex === "number"
                                ? "Update Batch"
                                : newBatch
                                ? "Add Batch"
                                : "Done"
                            }
                            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-[length:var(--primary-button-font-size)] font-[var(--primary-button-font-weight)]"
                            type="button"
                          />
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </Dialog.Panel>
            </Transition.Child>
          </div>

          <LoadingOverlay isLoading={loading} />
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PurchaseBatchesPanel;
//Should Add this as migrations to work

// - value           : the form field key (must match the panel field name)
// - label           : single label used for BOTH the label and the placeholder
// - enabled         : show / hide the field
// - full            : true  => full width (w-[100%]), false => half width (w-[50%])
// - childrenHeading : nested rows (used by the "summary" footer block)

// insert into 
// document_type_table(document_id,document_type_name)
// values ('01KV5FT7DGQQFBAXFTVY5J273F','purchase_batch_input_panel');
// INSERT INTO user_column_preferences (
//     id,
//     role_id,
//     document_id,
//     customization
// )
// VALUES (
//     '01KV5G1RG1FHZ7TYT5FZBEK526',
//     '01KKTGDYXG4BDSGT9TS0PZ5KB4',
//     '01KV5FT7DGQQFBAXFTVY5J273F',
//     '[
//       {
//         "label": "Batch No.",
//         "value": "item_batch_number",
//         "enabled": true,
//         "full": true
//       },
//       {
//         "label": "Expiry Date",
//         "value": "item_exp_date",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "To Bin",
//         "value": "to_bin_id",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Purchase Qty",
//         "value": "item_batch_pack_quantity",
//         "enabled": true,
//         "full": true
//       },
//       {
//         "label": "Pack Qty",
//         "value": "item_sellable_quantity",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Loose Qty",
//         "value": "item_batch_loose_quantity",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Free Qty",
//         "value": "item_batch_pack_free_quantity",
//         "enabled": true,
//         "full": true
//       },
//       {
//         "label": "Pack Qty",
//         "value": "item_batch_free_quantity",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Loose Qty",
//         "value": "item_batch_free_loose_quantity",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "MRP (Per 1 Qty)",
//         "value": "item_batch_unit_price",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Base PR (Per 1 Qty)",
//         "value": "item_batch_purchase_rate",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "PTR (Per 1 Qty)",
//         "value": "item_batch_ptr_amount",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "PTS (Per 1 Qty)",
//         "value": "item_batch_pts",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Scheme",
//         "value": "scheme",
//         "enabled": true,
//         "childrenHeading": [
//           {
//             "label": "Sch Qty",
//             "value": "item_batch_for_qauntity",
//             "enabled": true
//           },
//           {
//             "label": "Sch Free Qty",
//             "value": "item_batch_for_free_qauntity",
//             "enabled": true
//           },
//           {
//             "label": "Scheme %",
//             "value": "item_batch_scheme_discount_percentage",
//             "enabled": true
//           }
//         ]
//       },
//       {
//         "label": "Discount %",
//         "value": "item_batch_discount_percentage",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Tax %",
//         "value": "item_batch_tax_percentage",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Summary",
//         "value": "summary",
//         "enabled": true,
//         "childrenHeading": [
//           {
//             "label": "Final PR (Per Qty excl Tax)",
//             "value": "item_batch_purhcase_rate_before_tax",
//             "enabled": true
//           },
//           {
//             "label": "Final PR (Per 1 Qty inc Tax)",
//             "value": "item_batch_final_purchase_rate",
//             "enabled": true
//           },
//           {
//             "label": "Total Base PR",
//             "value": "item_batch_total_base_purchase_rate",
//             "enabled": true
//           },
//           {
//             "label": "Total Discount Amount",
//             "value": "item_batch_discount_amount",
//             "enabled": true
//           },
//           {
//             "label": "Taxable Amount",
//             "value": "item_batch_discounted_purchase_rate",
//             "enabled": true
//           },
//           {
//             "label": "Tax Amount",
//             "value": "item_batch_tax_amount",
//             "enabled": true
//           },
//           {
//             "label": "SGST",
//             "value": "item_batch_sgst_amount",
//             "enabled": true
//           },
//           {
//             "label": "CGST",
//             "value": "item_batch_cgst_amount",
//             "enabled": true
//           },
//           {
//             "label": "IGST",
//             "value": "item_batch_igst_amount",
//             "enabled": true
//           },
//           {
//             "label": "Payable or Total Amt",
//             "value": "item_batch_total_purchase_rate",
//             "enabled": true
//           }
//         ]
//       }
//     ]'::jsonb
// );
// INSERT INTO user_column_preferences_default_values (
//     document_id,
//     customization
// )
// VALUES (
//     '01KV5FT7DGQQFBAXFTVY5J273F',
//     '[
//       {
//         "label": "Batch No.",
//         "value": "item_batch_number",
//         "enabled": true,
//         "full": true
//       },
//       {
//         "label": "Expiry Date",
//         "value": "item_exp_date",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "To Bin",
//         "value": "to_bin_id",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Purchase Qty",
//         "value": "item_batch_pack_quantity",
//         "enabled": true,
//         "full": true
//       },
//       {
//         "label": "Pack Qty",
//         "value": "item_sellable_quantity",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Loose Qty",
//         "value": "item_batch_loose_quantity",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Free Qty",
//         "value": "item_batch_pack_free_quantity",
//         "enabled": true,
//         "full": true
//       },
//       {
//         "label": "Pack Qty",
//         "value": "item_batch_free_quantity",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Loose Qty",
//         "value": "item_batch_free_loose_quantity",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "MRP (Per 1 Qty)",
//         "value": "item_batch_unit_price",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Base PR (Per 1 Qty)",
//         "value": "item_batch_purchase_rate",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "PTR (Per 1 Qty)",
//         "value": "item_batch_ptr_amount",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "PTS (Per 1 Qty)",
//         "value": "item_batch_pts",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Scheme",
//         "value": "scheme",
//         "enabled": true,
//         "childrenHeading": [
//           {
//             "label": "Sch Qty",
//             "value": "item_batch_for_qauntity",
//             "enabled": true
//           },
//           {
//             "label": "Sch Free Qty",
//             "value": "item_batch_for_free_qauntity",
//             "enabled": true
//           },
//           {
//             "label": "Scheme %",
//             "value": "item_batch_scheme_discount_percentage",
//             "enabled": true
//           }
//         ]
//       },
//       {
//         "label": "Discount %",
//         "value": "item_batch_discount_percentage",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Tax %",
//         "value": "item_batch_tax_percentage",
//         "enabled": true,
//         "full": false
//       },
//       {
//         "label": "Summary",
//         "value": "summary",
//         "enabled": true,
//         "childrenHeading": [
//           {
//             "label": "Final PR (Per Qty excl Tax)",
//             "value": "item_batch_purhcase_rate_before_tax",
//             "enabled": true
//           },
//           {
//             "label": "Final PR (Per 1 Qty inc Tax)",
//             "value": "item_batch_final_purchase_rate",
//             "enabled": true
//           },
//           {
//             "label": "Total Base PR",
//             "value": "item_batch_total_base_purchase_rate",
//             "enabled": true
//           },
//           {
//             "label": "Total Discount Amount",
//             "value": "item_batch_discount_amount",
//             "enabled": true
//           },
//           {
//             "label": "Taxable Amount",
//             "value": "item_batch_discounted_purchase_rate",
//             "enabled": true
//           },
//           {
//             "label": "Tax Amount",
//             "value": "item_batch_tax_amount",
//             "enabled": true
//           },
//           {
//             "label": "SGST",
//             "value": "item_batch_sgst_amount",
//             "enabled": true
//           },
//           {
//             "label": "CGST",
//             "value": "item_batch_cgst_amount",
//             "enabled": true
//           },
//           {
//             "label": "IGST",
//             "value": "item_batch_igst_amount",
//             "enabled": true
//           },
//           {
//             "label": "Payable or Total Amt",
//             "value": "item_batch_total_purchase_rate",
//             "enabled": true
//           }
//         ]
//       }
//     ]'::jsonb
// );
 